// Administration → Journal: the audit log (GET /api/audit?limit=500, newest first) as a table
// (cards on phones) with filters by action and by person, a free-text search, relative times
// refreshed every minute and "AFFICHER PLUS" paging.
import { h, button, input, select, badge, emptyState } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { relativeTime, formatDateTime, truncate } from '../../format.js';
import { auditActionLabel, auditTone, formatAuditDetails, filterAudit, auditFacets } from './audit-format.js';
import { sectionHead, loadingState, errorState } from './common.js';
import './strings.js';

/** The server returns at most this many entries. */
export const AUDIT_LIMIT = 500;
/** Rows shown per page. */
export const AUDIT_PAGE = 100;
// Usernames match [a-z0-9._-], so these select values can never collide with one.
const ALL = '*all';
const SYSTEM = '*system';

/**
 * The `user` filter of filterAudit() for a select value (everyone / the system / a username).
 * @param {string} value
 * @returns {string|null}
 */
export function userFilterValue(value) {
  if (value === ALL || value === undefined || value === null) return null;
  if (value === SYSTEM) return '';
  return String(value);
}

/**
 * Creates the audit tab.
 * @param {object} ctx app context
 * @returns {{el: HTMLElement, reload(): Promise<void>, destroy(): void}}
 */
export function createAuditView(ctx) {
  let entries = null;
  let error = null;
  let destroyed = false;
  let shown = AUDIT_PAGE;
  const filters = { action: '', user: ALL, q: '' };

  const refreshBtn = button(t('adm.refresh'), { variant: 'secondary', icon: 'refresh', onClick: () => load() });
  const head = sectionHead({ title: t('adm.audit.title'), intro: t('adm.audit.intro'), actions: [refreshBtn] });
  const actionSel = select([{ value: '', label: t('adm.audit.allActions') }], { class: 'input-sm', 'aria-label': t('adm.audit.action') });
  const userSel = select([{ value: ALL, label: t('adm.audit.allUsers') }], { class: 'input-sm', 'aria-label': t('adm.audit.user') });
  const search = input({ type: 'search', class: 'input-sm', placeholder: t('adm.audit.searchPlaceholder'), 'aria-label': t('adm.audit.search') });
  const resetBtn = button(t('adm.audit.reset'), { variant: 'link', size: 'sm', onClick: () => resetFilters() });
  actionSel.addEventListener('change', () => { filters.action = actionSel.value; shown = AUDIT_PAGE; renderRows(); });
  userSel.addEventListener('change', () => { filters.user = userSel.value; shown = AUDIT_PAGE; renderRows(); });
  search.addEventListener('input', () => { filters.q = search.value; shown = AUDIT_PAGE; renderRows(); });
  const filterBar = h('div', { class: 'adm-audit-filters', role: 'search' },
    h('label', { class: 'adm-filter' }, h('span', { class: 'mono-label' }, t('adm.audit.action')), actionSel),
    h('label', { class: 'adm-filter' }, h('span', { class: 'mono-label' }, t('adm.audit.user')), userSel),
    h('label', { class: 'adm-filter adm-filter-grow' }, h('span', { class: 'mono-label' }, t('adm.audit.search')), search),
    resetBtn);
  const status = h('p', { class: 'adm-audit-status muted', 'aria-live': 'polite' });
  const body = h('div', { class: 'adm-body' }, loadingState());
  const el = h('section', { class: 'adm-view adm-audit' }, head, filterBar, status, body);

  async function load() {
    refreshBtn.disabled = true;
    try {
      const res = await ctx.api.get(ctx.api.withQuery('/api/audit', { limit: AUDIT_LIMIT }));
      if (destroyed) return;
      entries = (Array.isArray(res) ? res : Array.isArray(res?.entries) ? res.entries : [])
        .filter((e) => e && typeof e === 'object');
      error = null;
    } catch (err) {
      if (destroyed) return;
      error = err;
    } finally {
      if (!destroyed) refreshBtn.disabled = false;
    }
    render();
  }

  function resetFilters() {
    filters.action = '';
    filters.user = ALL;
    filters.q = '';
    search.value = '';
    shown = AUDIT_PAGE;
    render();
    search.focus?.();
  }

  function fillSelect(sel, options, value) {
    sel.replaceChildren(...options.map((o) => h('option', { value: o.value }, o.label)));
    sel.value = options.some((o) => o.value === value) ? value : options[0].value;
    return sel.value;
  }

  function render() {
    if (error && !entries) {
      filterBar.hidden = true;
      status.textContent = '';
      head.setCount('');
      refreshBtn.disabled = error.status === 404;
      body.replaceChildren(errorState(error, { onRetry: () => { body.replaceChildren(loadingState()); load(); }, unavailableText: t('adm.audit.unavailable') }));
      return;
    }
    if (!entries) return;
    filterBar.hidden = false;
    head.setCount(t('adm.audit.count', { n: entries.length }));
    const facets = auditFacets(entries);
    filters.action = fillSelect(actionSel, [{ value: '', label: t('adm.audit.allActions') },
      ...facets.actions.map((a) => ({ value: a.value, label: `${a.label} (${a.count})` }))], filters.action);
    filters.user = fillSelect(userSel, [{ value: ALL, label: t('adm.audit.allUsers') },
      ...facets.users.map((u) => ({ value: u.value === '' ? SYSTEM : u.value, label: `${u.label} (${u.count})` }))], filters.user);
    renderRows();
  }

  function renderRows() {
    if (!entries) return;
    const list = filterAudit(entries, { action: filters.action, user: userFilterValue(filters.user), q: filters.q });
    const filtered = list.length !== entries.length;
    resetBtn.hidden = !filtered;
    status.textContent = [
      filtered ? t('adm.audit.countOf', { n: list.length, total: entries.length }) : '',
      entries.length >= AUDIT_LIMIT ? t('adm.audit.limit') : '',
    ].filter(Boolean).join(' · ');
    if (!entries.length) {
      body.replaceChildren(emptyState({ icon: 'list', title: t('adm.audit.emptyLog') }));
      return;
    }
    if (!list.length) {
      body.replaceChildren(emptyState({ icon: 'search', title: t('adm.audit.empty'), action: button(t('adm.audit.reset'), { variant: 'secondary', onClick: () => resetFilters() }) }));
      return;
    }
    const now = Date.now();
    const th = (key) => h('th', { scope: 'col' }, t(`adm.audit.col.${key}`));
    const rows = list.slice(0, shown).map((e) => row(e, now));
    const more = list.length > shown
      ? button(`${t('adm.audit.more')} (${list.length - shown})`, { variant: 'secondary', class: 'adm-more', onClick: () => { shown += AUDIT_PAGE; renderRows(); } })
      : null;
    body.replaceChildren(h('div', { class: 'adm-table-wrap' },
      h('table', { class: 'table adm-table adm-audit-table' },
        h('thead', {}, h('tr', {}, th('when'), th('who'), th('action'), th('target'), th('details'))),
        h('tbody', {}, rows))), ...(more ? [more] : []));
  }

  function row(e, now) {
    const details = formatAuditDetails(e.details, undefined, e.action);
    const target = e.target ? String(e.target) : '';
    return h('tr', { class: 'adm-audit-row' },
      h('td', { class: 'adm-audit-when', 'data-label': t('adm.audit.col.when') },
        e.at ? h('time', { datetime: String(e.at), title: formatDateTime(e.at) }, relativeTime(e.at, now)) : '—'),
      h('td', { class: 'adm-audit-who', 'data-label': t('adm.audit.col.who') },
        e.username ? String(e.username) : h('span', { class: 'muted' }, t('adm.audit.system'))),
      h('td', { class: 'adm-audit-action', 'data-label': t('adm.audit.col.action') },
        badge(auditActionLabel(e.action), auditTone(e.action, e.details), { title: String(e.action || '') })),
      h('td', { class: 'adm-audit-target', 'data-label': t('adm.audit.col.target'), title: target || null },
        target ? h('code', { class: 'adm-code' }, truncate(target, 48)) : h('span', { class: 'muted' }, '—')),
      h('td', { class: 'adm-audit-details', 'data-label': t('adm.audit.col.details') },
        details || h('span', { class: 'muted' }, '—')));
  }

  // Relative times ("il y a 5 min") drift: refresh the rows every minute.
  // Only the <time> texts change, so the focus and the scroll position stay put.
  const timer = setInterval(() => {
    if (destroyed || !entries) return;
    const now = Date.now();
    for (const tm of body.querySelectorAll('time')) tm.textContent = relativeTime(tm.getAttribute('datetime'), now);
  }, 60_000);
  load();
  return {
    el,
    reload: load,
    destroy() {
      destroyed = true;
      clearInterval(timer);
    },
  };
}
